import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import components from "./components";
import store from "./storeGlobal/index";
import directives from "./directives/index";
import "./assets/main.css";
import { vMaska } from "maska";
import { autoAnimatePlugin } from "@formkit/auto-animate/vue";
// import Hotjar from "@hotjar/browser";
import VueGtag from "vue-gtag";
import "intersection-observer";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginVue from "@bugsnag/plugin-vue";
import BugsnagPerformance from "@bugsnag/browser-performance";
import axiosInstance from "@/helpers/axios";
import { saleName } from "@/helpers/saleUtils";

// A password-reset link is an explicit request for a new session. Any token
// this browser still holds (often a stale one) is dropped before the app
// mounts: otherwise the guest-only guard sends the link to the sales board,
// and the sidebar's /profile call with that token gets a 401 that logs the
// visitor out to /login before the page is ever seen.
if (/^\/new-password\//.test(window.location.pathname)) {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
}

const app = createApp(App).directive("maska", vMaska);

// Available in every template as $saleName(sale)
app.config.globalProperties.$saleName = saleName;

components.forEach((component) => {
  app.component(component.name, component);
});

directives.forEach((directive) => {
  app.directive(directive.name, directive);
});

Bugsnag.start({
  apiKey: "f56df16859bd97c585f6f19f0a583c84",
  plugins: [new BugsnagPluginVue()],
});
BugsnagPerformance.start({ apiKey: "f56df16859bd97c585f6f19f0a583c84" });

const bugsnagVue = Bugsnag.getPlugin("vue");
bugsnagVue.installVueErrorHandler(app);

const siteId = 5105246;
// const hotjarVersion = 6;

// Hotjar.init(siteId, hotjarVersion);

app.use(router);

app.use(store);
app.config.devtools = true;

app.use(autoAnimatePlugin);

app.use(
  VueGtag,
  {
    config: { id: import.meta.env.VITE_GOOGLE_TAG_ID },
  },
  router
);

// Whether a request went out with the Authorization header that the request
// interceptor in helpers/axios.js adds when a token is stored.
const sentWithToken = (config) => {
  const headers = config?.headers;
  if (!headers) return false;
  const value =
    typeof headers.get === "function"
      ? headers.get("Authorization")
      : headers.Authorization;
  return Boolean(value);
};

axiosInstance.interceptors.response.use(
  (response) => {
    // Interceptor hit for successful response
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // A 401 ends the session only when the request carried the token: it
      // expired, was revoked, or the password changed elsewhere. A 401 on a
      // request sent without one (a guest page's /profile, a wrong login
      // password) must not log anyone out. The old check on the router path
      // raced the first page load and bounced the reset-password link to /login.
      if (
        sentWithToken(error.config) &&
        store &&
        typeof store.dispatch === "function"
      ) {
        store.dispatch("logout");
      }
    }
    // The API refuses everything except /profile and the password change itself
    // while the customer still has to update the password: open the popup.
    if (
      error.response &&
      error.response.status === 423 &&
      error.response.data?.code === "password_change_required"
    ) {
      // The API only answers 423 once the deadline has passed: always the blocking mode.
      // Many requests are refused at once: count the block once.
      if (!store.getters["forcePasswordChange/isEnforced"]) {
        app.config.globalProperties.$gtag?.event("password_change_required_423", {
          path: router.currentRoute.value.path,
        });
      }
      store.dispatch("forcePasswordChange/require", { enforced: true });
    }
    return Promise.reject(error);
  }
);

// Global handler for unhandled promise rejections (for debugging)
window.addEventListener("unhandledrejection", function (event) {
  console.error("Unhandled promise rejection:", event.reason);
});

app.mount("#app");
